import { createContext, useContext, type ReactNode } from "react";
import { en } from "./i18n/en";
import { da } from "./i18n/da";

/**
 * Minimal i18n: dictionaries keyed by message id; the tenant default locale comes from settings.
 * Apps register their own messages with `registerMessages(locale, dict)`; the shared UI strings and the
 * status language (HANDOFF §2.3) ship in ./i18n/en.ts and ./i18n/da.ts.
 * Placeholders use `{name}` and are replaced by `vars`.
 */
export type Dict = Record<string, string>;
export const dictionaries: Record<string, Dict> = { en: { ...en }, da: { ...da } };
export const LocaleContext = createContext<string>("en");

/** BCP 47 tag for a UI locale ("en" → "en-GB", "da" → "da-DK"); full tags pass through. */
export function localeTag(locale: string): string {
  return locale === "en" ? "en-GB" : locale === "da" ? "da-DK" : locale;
}

let activeLocale = "en";
/** Locale of the innermost `LocaleProvider` (for non-hook code such as `fmtDate`). */
export function getActiveLocale(): string { return activeLocale; }
export function setActiveLocale(locale: string): void { activeLocale = locale; }

/** `LocaleContext.Provider` that also records the locale for the non-hook formatters (`fmtDate`, `timeAgo`, …). */
export function LocaleProvider({ value, children }: { value: string; children: ReactNode }) {
  if (activeLocale !== value) activeLocale = value;   // during render so the same pass formats in the new locale
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function registerMessages(locale: string, messages: Dict): void {
  dictionaries[locale] = { ...(dictionaries[locale] ?? {}), ...messages };
}

/** True when the id exists in the locale or in the English fallback. */
export function hasMessage(id: string, locale = "en"): boolean {
  return dictionaries[locale]?.[id] !== undefined || dictionaries.en?.[id] !== undefined;
}

export function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
  return s;
}

/** Non-hook translate (for code outside components). Falls back to English, then to the id itself. */
export function translate(locale: string, id: string, vars?: Record<string, string | number>): string {
  return interpolate(dictionaries[locale]?.[id] ?? dictionaries.en?.[id] ?? id, vars);
}

export function useLocale(): string {
  return useContext(LocaleContext);
}

export function useT() {
  const locale = useContext(LocaleContext);
  return (id: string, vars?: Record<string, string | number>): string => translate(locale, id, vars);
}
